import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { localize } from "src/l10n";
import api from "src/spintr/SpintrApi";
import { ActionMenu, Label, PageHeader } from "src/ui";
import "./SystemStatusesPanel.scss";
import { Modal } from "@fluentui/react";
import PopupHeader from "src/ui/components/PopupHeader";
import { IActiveUserProfile } from "src/profile/reducer";
import { Link, useHistory, useLocation } from "react-router-dom";
import SpintrLoader from "src/ui/components/Loader";
import SystemStatusResourceInfo from "./SystemStatusResourceInfo";
import SystemStatusForm from "./SystemStatusForm";
import Axios from "axios";
import { SpintrTypes } from "src/typings";

interface IProps {
    currentUser?: IActiveUserProfile;
}

interface ISystemStatusResourceCategory {
    id: number;
    name: string;
    isActive?: boolean;
    resources: Spintr.ISystemStatusResource[];
}

const SystemStatusesPanel: FunctionComponent<IProps> = ({ currentUser }) => {
    const history = useHistory();
    const location = useLocation();

    const [resources, setResources] = useState([]);
    const [showNewResourceModal, setShowNewResourceModal] = useState(false);
    const [newResource, setNewResource] = useState<Spintr.ISystemStatusResource>();
    const [isLoading, setIsLoading] = useState(false);
    const [helpContent, setHelpContent] = useState<Spintr.HelpContent | undefined>(undefined);

    useEffect(() => {
        const cancelTokenSource = Axios.CancelToken.source();

        api.get<Spintr.HelpContent>("/api/v1/help/10", { cancelToken: cancelTokenSource.token })
            .then((response) => setHelpContent(response.data))
            .catch(() => {
                // Ignore
            });

        return () => cancelTokenSource.cancel();
    }, [setHelpContent]);

    const fetchResources = useCallback((searchText?: string) => {
        api.get("/api/v1/systemstatuses/resources", {
            params: {
                searchText,
            },
        }).then((response) => {
            setResources(response.data);
            setIsLoading(false);
        });
    }, []);

    useEffect(() => {
        fetchResources();
    }, []);

    const toggleShowNewResourceModal = (id?: number) => {
        // @ts-ignore
        setNewResource({
            id,
            targets: [],
            owners: [
                {
                    id: currentUser.id,
                    key: currentUser.id,
                    name: currentUser.name,
                    imageUrl: currentUser.images.feedComposer,
                    subText: currentUser.department.name,
                },
            ],
            faq: [],
            categoryId: -1
        });
        setShowNewResourceModal(!showNewResourceModal);
    };

    const data : ISystemStatusResourceCategory[] = useMemo(() => {
        const activeResources = resources.filter(
            (x) => x.status == SpintrTypes.SystemStatusType.Ongoing,
        );

        const plannedResources = resources.filter(
            (x) => x.status == SpintrTypes.SystemStatusType.Planned,
        );

        let data: ISystemStatusResourceCategory[] = [];

        for (let resource of resources) {
            if (activeResources.find(x => x.id === resource.id) ||
                plannedResources.find(x => x.id === resource.id)) {
                continue;
            }

            const foundCategory = data.find(x => x.id === resource.categoryId);

            if (foundCategory) {
                foundCategory.resources.push(resource);
            } else {
                data.push({
                    id: resource.categoryId,
                    name: resource.categoryName || localize("Alla"),
                    isActive: false,
                    resources: [resource]
                })
            }
        }

        const allCategory = data.find(x => !x.id);

        data = data.filter(x => x.id).sort((a, b) => (a.name > b.name ? 1 : -1));

        const result = [
            ...(activeResources.length > 0 ?
                [{
                    id: 0,
                    name: localize("Aktiva"),
                    isActive: true,
                    resources: activeResources
                }] :
                []
            ),
            ...(plannedResources.length > 0 ?
                [{
                    id: -1,
                    name: localize("Planerade"),
                    isActive: true,
                    resources: plannedResources
                }] :
                []
            ),
            ...data,
        ];

        if (allCategory) {
            result.push(allCategory);
        }

        console.log(result);

        return result;
    }, [resources]);

    const isStartView = history.location.pathname === "/operations";

    return (
        <div className={[
            "SystemStatusesPanel",
            isStartView ? "start-view" : ""
        ].join(" ")}>
            <div className="header">
                <PageHeader
                    title={isStartView ? "" : localize("appSystemStatus")}
                    subText={isStartView ? "" : helpContent?.text}
                    displaySearch
                    displayBackButton={!isStartView}
                    forceExpandedSearch={isStartView}
                    onBackButtonClick={() => {
                        history.push({
                            pathname: "/operations"
                        });
                    }}
                    onSearchQueryChange={(value: string) => {
                        fetchResources(value);
                    }}
                    buttons={currentUser.isAdmin ? [{
                        key: "add",
                        title: localize("SkapaNy"),
                        icon: "add",
                        onClick: () => {
                            toggleShowNewResourceModal();
                        },
                        theme: "primary"
                    }] : []}
                />
            </div>
            {isLoading && <SpintrLoader />}
            {!isLoading && (
                <div className="categories">
                    {data.map((c: ISystemStatusResourceCategory, index: number) => (
                        <div key={index} className="category">
                            <Label
                                color="primaryContent"
                                size="small-1"
                                weight="medium"
                                className="ConversationsPanel-subline">
                                {c.name}
                            </Label>
                            <div className="resources">
                                {c.resources.map((r) => (
                                    <Link
                                        key={r.id}
                                        className={[
                                            "ConversationItem",
                                            ...(location.pathname === "/operations/" + r.id ? ["active"] : [])
                                        ].join(" ")}
                                        to={"/operations/" + r.id}>
                                        <SystemStatusResourceInfo resource={r} />
                                        {r.userCanEdit && (
                                            <ActionMenu
                                                categories={[{
                                                    items: [{
                                                        text: localize("Redigera"),
                                                        onClick: () => {
                                                            toggleShowNewResourceModal(r.id);
                                                        },
                                                    }],
                                                }]}
                                            />
                                        )}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <Modal
                className="spintr-modal modalWithPopupHeader system-status-resource-modal"
                isOpen={showNewResourceModal}
                onDismiss={() => {
                    setShowNewResourceModal(false);
                }}
            >
                <PopupHeader
                    text={localize("SYSTEM_STATUS_RESOURCES_MODAL_HEADER_" + (newResource?.id ? "EDIT" : "CREATE"))}
                    onClose={() => {
                        setShowNewResourceModal(false);
                    }}
                />
                <div className="popup-inner">
                    <SystemStatusForm
                        id={newResource?.id}
                        onDone={() => {
                            setShowNewResourceModal(false);
                            fetchResources();
                        }}
                        onCancel={() => {
                            setShowNewResourceModal(false);
                        }} />
                </div>
            </Modal>
        </div>
    );
};

const mapStateToProps = (state: Spintr.AppState, props) => ({
    ...props,
    currentUser: state.profile.active,
});

export default connect(mapStateToProps)(SystemStatusesPanel);
